import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { Geolocation } from '@capacitor/geolocation';

import API from '@api/API';

import { useCurrentUser } from '@queries/UserQueries';

import { useAppContext } from '../contexts/AppContext';
import { useDevice } from './useDevice';

export interface Coordinates {
  lat: number;
  lng: number;
}

const DENVER_COORDS: Coordinates = {
  lat: 39.7392358,
  lng: -104.990251,
};

const CHICAGO_COORDS: Coordinates = {
  lat: 41.8781136,
  lng: -87.6297982,
};

const PALM_SPRINGS_COORDS: Coordinates = {
  lat: 33.8244857,
  lng: -116.5371162,
};

const BOSTON_COORDS: Coordinates = {
  lat: 42.3600825,
  lng: -71.0588801,
};

const checkLocationPermissionsWeb = async (): Promise<boolean> =>
  new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      () => resolve(true),
      (positionError) => {
        if (positionError.code === positionError.PERMISSION_DENIED) {
          resolve(false);
        }

        console.log(positionError);

        resolve(true);
      },
    );
  });

const fetchLocationWeb = (): Promise<Coordinates> =>
  new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (positionError) => {
        if (positionError.code === positionError.PERMISSION_DENIED) {
          console.warn('User denied geolocation permissions');
        }
        console.log(positionError);

        resolve(DENVER_COORDS);
      },
    );
  });

const fetchLocationNative = async (): Promise<Coordinates> => {
  const { coords } = await Geolocation.getCurrentPosition();

  return {
    lat: coords.latitude,
    lng: coords.longitude,
  };
};

const checkLocationPermissionsNative = async (): Promise<boolean> => {
  const { location } = await Geolocation.checkPermissions();

  return location !== 'denied';
};

const getCurrentPosition = async (context: string) => {
  try {
    console.log(context);
    console.trace();
    const permStatus = await Geolocation.checkPermissions();
    if (permStatus.location === 'prompt' || permStatus.location === 'prompt-with-rationale') {
      await Geolocation.requestPermissions();
    }
    const { coords } = await Geolocation.getCurrentPosition();

    return { coords };
  } catch (e: any) {
    const { code } = e ?? {};

    if (code === 1) {
      const storedCityOverride = localStorage.getItem('currentCityOverride');

      if (storedCityOverride) {
        const parsedStoredCityOverride: CurrentCity = JSON.parse(storedCityOverride);

        if (parsedStoredCityOverride?.coordinates) {
          return {
            coords: {
              latitude: parsedStoredCityOverride.coordinates.lat,
              longitude: parsedStoredCityOverride.coordinates.lng,
            },
          };
        }
      }
    }

    console.log(e);
  }
};

const LOCATION_ENABLED = false;

export const useCurrentUserLocation = (options: {
  context: string;
  forceDisable?: boolean;
  forceFetch?: boolean;
}) => {
  const { userIsLoggedIn, isLocationPermissionGateDisabled, currentCityOverride } = useAppContext();

  const fetchCurrentLocation = async (): Promise<Coordinates> => {
    if (options.forceDisable) {
      return {
        lat: 0,
        lng: 0,
      };
    }

    if (currentCityOverride) {
      return {
        lat: currentCityOverride.coordinates.lat,
        lng: currentCityOverride.coordinates.lng,
      };
    }

    const { coords } = await getCurrentPosition(`${options.context}::fetchCurrentLocation`);

    const storedCityOverride = localStorage.getItem('currentCityOverride');

    if (!coords && storedCityOverride) {
      const parsedStoredCityOverride: CurrentCity = JSON.parse(storedCityOverride);

      if (parsedStoredCityOverride?.coordinates) {
        return {
          lat: parsedStoredCityOverride.coordinates.lat,
          lng: parsedStoredCityOverride.coordinates.lng,
        };
      }
    }

    return {
      lat: coords.latitude,
      lng: coords.longitude,
    };
  };

  const {
    isFetched: currentLocationIsReady,
    data: currentLocation,
    refetch,
  } = useQuery({
    queryKey: ['currentLocation', currentCityOverride?.coordinates],
    queryFn: fetchCurrentLocation,
    enabled: Boolean(userIsLoggedIn && !isLocationPermissionGateDisabled) || options?.forceFetch,
    staleTime: 1000 * 60 * 15, // 15 minutes
  });

  const checkCurrentLocationPermissions = async (): Promise<boolean> => {
    try {
      const { coords } = await getCurrentPosition(
        `${options.context}::checkCurrentLocationPermissions`,
      );

      if (coords?.latitude && coords?.longitude) {
        return true;
      }
    } catch (e) {
      console.log(e);
    }

    const { location } = await Geolocation.checkPermissions();

    return location === 'granted';
  };

  return {
    currentLocationIsReady,
    currentLocation,
    refetchCurrentLocation: refetch,
    checkCurrentLocationPermissions,
  };
};

export interface CurrentCity {
  _id: string;
  name: string;
  slug?: string;
  eventsAreEnabled: boolean;
  coordinates: Coordinates;
  areasOfImprovementEnabled?: boolean;
  ratingsLeaderboardEnabled?: boolean;
  features?: {
    areasOfImprovementEnabled?: boolean;
    ratingsLeaderboardEnabled?: boolean;
    boostProfileEnabled?: boolean;
  };
  featuredSeries?: {
    drag?: string;
    bipoc?: string;
    kingsThings?: string;
    sapphic?: string;
    dragOpenStages?: string;
  };
}

export const useCurrentUserCity = (options: { forceFetch?: boolean } = {}) => {
  const { forceFetch = false } = options;
  const { currentUser } = useCurrentUser();

  const { currentLocationIsReady, currentLocation } = useCurrentUserLocation({
    context: 'useCurrentUserCity',
    forceDisable: !currentUser?._id,
    forceFetch,
  });
  const { lat, lng } = currentLocation ?? {};

  const { isFetched, data, refetch } = useQuery({
    queryKey: useCurrentUserCity.queryKey(currentLocation),
    queryFn: () =>
      API.get<{
        city: CurrentCity;
        primaryCity: CurrentCity;
        cities: CurrentCity[];
        multiCityEnabled?: boolean;
      }>(`/v1/city/me?lat=${lat}&lng=${lng}`),
    enabled: Boolean(currentLocationIsReady && lat && lng) || forceFetch,
  });

  return {
    currentCityIsReady: isFetched,
    currentCity: data?.city,
    primaryCity: data?.primaryCity,
    cities: data?.cities,
    multiCityEnabled: data?.multiCityEnabled,
    refetchCity: refetch,
  };
};
useCurrentUserCity.queryKey = ({ lat, lng }: Coordinates = {} as Coordinates) => [
  'user',
  'city',
  lat,
  lng,
];

export const useCitiesQuery = (isEnabled = true) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useCitiesQuery.queryKey(),
    queryFn: () => useCitiesQuery.queryFn(),
    enabled: isEnabled,
  });

  return {
    citiesAreReady: isFetched,
    cities: data?.cities,
    refetchCities: refetch,
  };
};

useCitiesQuery.queryKey = () => ['cities'];
useCitiesQuery.queryFn = () => API.get<{ cities: CurrentCity[] }>(`/v1/cities`);

export const useCityById = (cityId: string) => {
  const { cities } = useCitiesQuery();

  const { isFetched, data } = useQuery({
    queryKey: useCityById.queryKey(cityId),
    queryFn: () => API.get<{ city: CurrentCity }>(`/v1/cities/${cityId}`),
    enabled: Boolean(cityId),
  });

  const city = useMemo(() => {
    if (data?.city) {
      return data.city;
    }

    return cities?.find((city) => city._id === cityId);
  }, [cities, cityId, data?.city]);

  return {
    cityIsReady: isFetched,
    city,
  };
};

useCityById.queryKey = (cityId: string) => ['city', cityId];
